import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Plus, Trash2, ArrowLeft } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getDashboard, createDashboard, updateDashboard, BIDashboard, BIWidget } from '../api/bi';
import BILayout from '../components/BILayout';

const chartTypes = [
  { value: 'kpi', label: 'KPI (число)' },
  { value: 'bar', label: 'Столбчатая диаграмма' },
  { value: 'line', label: 'Линейная диаграмма' },
  { value: 'area', label: 'Областная диаграмма' },
  { value: 'pie', label: 'Круговая диаграмма' },
  { value: 'funnel', label: 'Воронка' },
  { value: 'radial', label: 'Радиальная диаграмма' },
  { value: 'treemap', label: 'Древовидная карта' },
  { value: 'scatter', label: 'Точечная диаграмма' },
  { value: 'stackedBar', label: 'Столбчатая с накоплением' },
  { value: 'heatmap', label: 'Тепловая карта' },
  { value: 'gauge', label: 'Спидометр' },
  { value: 'table', label: 'Таблица' }
];

const presetColors = [
  '#ffffff', '#f8f9fa', '#e3f2fd', '#e8f5e9', '#fff3e0', '#fce4ec',
  '#e0f7fa', '#f3e5f5', '#e8eaf6', '#ffebee', '#fff8e1', '#f1f8e9'
];

const BIDashboardBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [widgets, setWidgets] = useState<BIWidget[]>([]);
  const [loading, setLoading] = useState(false);
  const [customColor, setCustomColor] = useState('');

  useEffect(() => {
    if (id && id !== 'new') loadDashboard();
  }, [id]);

  const loadDashboard = async () => {
    try {
      const res = await getDashboard(id!);
      setName(res.data.name);
      setDescription(res.data.description || '');
      setWidgets(res.data.widgets || []);
    } catch (err) {
      showToast('Не удалось загрузить дашборд', 'error');
    }
  };

  const addWidget = () => {
    const newWidget: BIWidget = {
      id: Date.now().toString(),
      type: 'kpi',
      title: 'Новый виджет',
      size: 'medium',
      userData: [{ name: 'Пример', value: 100 }],
      userDataRaw: '',
      cardColor: '#ffffff',
      params: { dataMode: 'simple' },
      position: widgets.length,
      layout: { x: 0, y: 0, w: 2, h: 2 }
    };
    setWidgets([...widgets, newWidget]);
  };

  const updateWidget = (index: number, field: keyof BIWidget, value: any) => {
    const updated = [...widgets];
    updated[index] = { ...updated[index], [field]: value };
    setWidgets(updated);
  };

  const removeWidget = (index: number) => {
    setWidgets(widgets.filter((_, i) => i !== index));
  };

  const parseAndUpdateWidgetData = (idx: number, widget: BIWidget) => {
    const raw = widget.userDataRaw || '';
    if (!raw.trim()) return;
    const lines = raw.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return;

    const result = [];
    const firstLine = lines[0];
    let delimiter = '\t';
    if (firstLine.includes(',')) delimiter = ',';
    else if (firstLine.includes(' ')) delimiter = ' ';

    const partsFirst = firstLine.split(delimiter);
    const hasHeader = partsFirst.length >= 2 && isNaN(parseFloat(partsFirst[1]));
    const startIdx = hasHeader ? 1 : 0;

    for (let i = startIdx; i < lines.length; i++) {
      const parts = lines[i].split(delimiter);
      let name = parts[0]?.trim();
      let value = parseFloat(parts[1]?.trim());
      if (isNaN(value)) value = 0;
      if (name) result.push({ name, value });
    }
    if (result.length) {
      updateWidget(idx, 'userData', result);
      showToast(`Загружено ${result.length} строк данных`, 'success');
    } else {
      showToast('Не удалось распознать данные. Проверьте формат (название и значение).', 'error');
    }
  };

  const saveDashboard = async () => {
    if (!name.trim()) {
      showToast('Введите название дашборда', 'error');
      return;
    }
    setLoading(true);
    try {
      const data = { name, description, widgets };
      let savedId: string;
      if (id && id !== 'new') {
        await updateDashboard(id, data);
        savedId = id;
        showToast('Дашборд обновлён', 'success');
      } else {
        const res = await createDashboard(data);
        savedId = res.data._id;
        showToast('Дашборд создан', 'success');
      }
      navigate(`/app/bi/viewer/${savedId}`);
    } catch (err) {
      showToast('Не удалось сохранить дашборд', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BILayout>
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate('/app/bi')} style={styles.backButton}>
            <ArrowLeft size={18} /> Назад
          </button>
          <h1 style={styles.title}>{id === 'new' ? 'Создание дашборда' : 'Редактирование дашборда'}</h1>
          <button onClick={saveDashboard} disabled={loading} style={styles.saveButton}>
            <Save size={18} /> {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>

        <div style={styles.form}>
          <input
            type="text"
            placeholder="Название дашборда"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
          />
          <textarea
            placeholder="Описание (необязательно)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={styles.textarea}
          />
        </div>

        <div style={styles.widgetsHeader}>
          <h3>Виджеты</h3>
          <button onClick={addWidget} style={styles.addWidgetButton}>
            <Plus size={16} /> Добавить виджет
          </button>
        </div>

        <div style={styles.widgetsGrid}>
          {widgets.map((widget, idx) => (
            <div key={widget.id} style={styles.widgetCard}>
              <div style={styles.widgetHeader}>
                <input
                  type="text"
                  value={widget.title}
                  onChange={(e) => updateWidget(idx, 'title', e.target.value)}
                  style={styles.widgetTitleInput}
                />
                <button onClick={() => removeWidget(idx)} style={styles.removeWidgetButton}>
                  <Trash2 size={16} />
                </button>
              </div>

              <select
                value={widget.type}
                onChange={(e) => updateWidget(idx, 'type', e.target.value as any)}
                style={styles.select}
              >
                {chartTypes.map(ct => (
                  <option key={ct.value} value={ct.value}>{ct.label}</option>
                ))}
              </select>

              {/* Выбор цвета карточки */}
              <div style={styles.colorSection}>
                <label style={styles.label}>Цвет карточки:</label>
                <div style={styles.colorPalette}>
                  {presetColors.map(color => (
                    <div
                      key={color}
                      style={{
                        ...styles.colorSwatch,
                        backgroundColor: color,
                        border: widget.cardColor === color ? '2px solid #2575fc' : '1px solid #ddd'
                      }}
                      onClick={() => updateWidget(idx, 'cardColor', color)}
                    />
                  ))}
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => {
                      setCustomColor(e.target.value);
                      updateWidget(idx, 'cardColor', e.target.value);
                    }}
                    style={styles.colorPicker}
                    title="Свой цвет"
                  />
                </div>
              </div>

              {/* Ручной ввод данных */}
              <div style={styles.dataSourceBlock}>
                <label style={styles.label}>Ручной ввод данных:</label>
                <div style={styles.dataInputMode}>
                  <button
                    type="button"
                    style={{
                      ...styles.modeButton,
                      backgroundColor: widget.params?.dataMode === 'simple' ? '#2575fc' : '#e0e0e0',
                      color: widget.params?.dataMode === 'simple' ? 'white' : '#333'
                    }}
                    onClick={() => updateWidget(idx, 'params', { ...widget.params, dataMode: 'simple' })}
                  >
                    Простой ввод (из Excel)
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.modeButton,
                      backgroundColor: widget.params?.dataMode !== 'simple' ? '#2575fc' : '#e0e0e0',
                      color: widget.params?.dataMode !== 'simple' ? 'white' : '#333'
                    }}
                    onClick={() => updateWidget(idx, 'params', { ...widget.params, dataMode: 'json' })}
                  >
                    Редактировать JSON
                  </button>
                </div>
              </div>

              {widget.params?.dataMode === 'simple' ? (
                <div>
                  <textarea
                    rows={6}
                    placeholder="Вставьте данные из Excel или введите в формате:&#10;Название\tЗначение&#10;Январь\t100&#10;Февраль\t200&#10;Март\t150&#10;&#10;Разделители: табуляция, запятая или пробел"
                    value={widget.userDataRaw || ''}
                    onChange={(e) => updateWidget(idx, 'userDataRaw', e.target.value)}
                    style={styles.simpleDataInput}
                  />
                  <div style={styles.buttonGroup}>
                    <button
                      onClick={() => parseAndUpdateWidgetData(idx, widget)}
                      style={styles.parseButton}
                    >
                      Применить данные
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const sampleData = `Название\tЗначение\nЯнварь\t120\nФевраль\t200\nМарт\t150\nАпрель\t180\nМай\t220`;
                        updateWidget(idx, 'userDataRaw', sampleData);
                      }}
                      style={styles.sampleButton}
                    >
                      📋 Вставить пример
                    </button>
                    <a href="/sample-data.xlsx" download style={styles.sampleLink}>
                      📎 Скачать пример Excel
                    </a>
                  </div>
                  {widget.userData && widget.userData.length > 0 && (
                    <div style={styles.preview}>
                      <strong>Предпросмотр данных:</strong>
                      <pre>{JSON.stringify(widget.userData, null, 2)}</pre>
                    </div>
                  )}
                </div>
              ) : (
                <textarea
                  rows={6}
                  value={JSON.stringify(widget.userData || [], null, 2)}
                  onChange={(e) => {
                    try {
                      const data = JSON.parse(e.target.value);
                      updateWidget(idx, 'userData', data);
                      updateWidget(idx, 'userDataRaw', '');
                    } catch (err) {}
                  }}
                  style={styles.jsonTextarea}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </BILayout>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  backButton: {
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '30px',
    padding: '8px 20px',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0
  },
  saveButton: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    padding: '10px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },
  form: { marginBottom: '30px' },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.8)',
    fontSize: '16px',
    marginBottom: '12px'
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.8)',
    fontSize: '14px',
    minHeight: '80px'
  },
  widgetsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  addWidgetButton: {
    background: 'rgba(37,117,252,0.1)',
    border: '1px dashed #2575fc',
    color: '#2575fc',
    borderRadius: '30px',
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  widgetsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '20px' },
  widgetCard: {
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(8px)',
    borderRadius: '16px',
    padding: '16px',
    border: '1px solid rgba(0,0,0,0.1)',
    overflow: 'auto',
    maxWidth: '100%'
  },
  widgetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  widgetTitleInput: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '16px',
    fontWeight: 600,
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: '8px',
    background: 'white'
  },
  removeWidgetButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', padding: '6px' },
  select: {
    width: '100%',
    padding: '8px 12px',
    marginBottom: '10px',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'white'
  },
  colorSection: { marginBottom: '12px' },
  colorPalette: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '6px' },
  colorSwatch: { width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', transition: 'transform 0.2s' },
  colorPicker: { width: '36px', height: '36px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #ddd' },
  label: { display: 'block', marginBottom: '6px', fontSize: '13px', color: '#666' },
  dataSourceBlock: { marginTop: '8px', marginBottom: '12px' },
  dataInputMode: { display: 'flex', gap: '8px', marginTop: '6px' },
  modeButton: { padding: '6px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500 },
  simpleDataInput: {
    width: '100%',
    maxWidth: '100%',
    padding: '8px',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    fontFamily: 'monospace',
    fontSize: '12px',
    marginBottom: '8px',
    overflowX: 'auto',
    resize: 'vertical'
  },
  jsonTextarea: {
    width: '100%',
    maxWidth: '100%',
    padding: '8px',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'white',
    fontFamily: 'monospace',
    fontSize: '12px',
    resize: 'vertical'
  },
  parseButton: { background: '#2575fc', color: 'white', border: 'none', borderRadius: '20px', padding: '6px 12px', cursor: 'pointer', marginRight: '8px' },
  sampleButton: { background: '#f0f0f0', border: '1px solid #ccc', borderRadius: '20px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' },
  sampleLink: { fontSize: '12px', color: '#2575fc', textDecoration: 'none', marginLeft: '8px' },
  buttonGroup: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' },
  preview: { background: '#f5f5f5', padding: '8px', borderRadius: '8px', fontSize: '11px', overflowX: 'auto' }
};

export default BIDashboardBuilder;